/** @jsx jsx */
import { jsx } from "theme-ui"
import PropTypes from "prop-types"
import styled from "styled-components"
import ButtonLink from "@components/ButtonLink"
import theme, { rem, articleStyles } from "@src/theme"
import IconLink from "@components/IconLink"
import {
  Accordion as LibraryAccordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from "react-accessible-accordion"
import "react-accessible-accordion/dist/fancy-example.css"
import { formatMarkdownToHtml } from "@helpers"
import { Themed } from "@theme-ui/mdx"
import { HorizontalBarChart } from "@components/BarChart"
import { PieChart } from "@components/PieChart"

const Panel = ({
  id,
  body,
  heading,
  ctaLabel,
  ctaLink,
  image,
  colorSet,
  chartsTag = '',
  charts = [],
  noBg = false,
  links,
  noMaxWidth = false,
}) => {
  const displayCharts = !!chartsTag
  const taggedCharts = charts.filter(({ tag }) => tag === chartsTag)

  return (
    <PanelStyleWrapper
      sx={{
        bg: noBg ? 'transparent' : colorSet.bg,
        color: colorSet.text,
        maxWidth: noMaxWidth ? "unset" : ["100%", null, null, "66%"],
        mx: noMaxWidth ? "auto" : [rem(48), null, null, "auto"],
        border: `1px solid ${theme.colors[colorSet.text]}`,
        borderBottom: "none",
        ":last-child": {
          borderBottom: `1px solid ${theme.colors[colorSet.text]}`,
        },
        ...(displayCharts ? {
          '.content-inner': {
            width: '100%',
            paddingRight: 0
          }
        } : {})
      }}
      noBg={noBg}
      colorSet={colorSet}
    >
      <AccordionItem>
        <AccordionItemHeading>
          <AccordionItemButton>{heading}</AccordionItemButton>
        </AccordionItemHeading>
        <AccordionItemPanel>
          <div
            sx={{
              display: "flex",
              flexDirection: ["column", null, "row"],
            }}
          >
            <div
              className="content-inner"
              sx={{
                color: colorSet.text,
                pt: 0,
                pr: [0, null, rem(48), null],
                width: image ? ["90%", null, "50%"] : "90%",
              }}
            >
              <div
                dangerouslySetInnerHTML={{ __html: body }}
                sx={{
                  ...articleStyles,
                  pb: 0,
                }}
              />
              {displayCharts && (
                <div>
                  {taggedCharts.map(block => {
                    switch (block.type) {
                      case 'barGraph':
                        return (
                          <div sx={{ p: 4, bg: 'white', color: 'purple', my: '1.5rem' }}>
                            <Themed.h4 sx={{ textTransform: 'none', m: 0, mb: 2 }}>{block.heading}</Themed.h4>
                            {block.description && <p sx={{ color: 'darkPurple' }}>{block.description}</p>}
                            <HorizontalBarChart data={block.data} />
                          </div>
                        )
                      case 'pieChart':
                        return (
                          <div sx={{ p: 4, bg: 'white', color: 'purple', my: '1.5rem' }}>
                            <Themed.h4 sx={{ textTransform: 'none', m: 0, mb: 2 }}>{block.heading}</Themed.h4>
                            {block.description && <p sx={{ color: 'darkPurple' }}>{block.description}</p>}
                            <PieChart data={block.data} />
                          </div>
                        )
                      default:
                        return null
                    }
                  })}
                </div>
              )}
              {ctaLink && ctaLabel && (
                <div
                  sx={{
                    mt: [0, null, rem(48)],
                    mb: links && links.length ? rem(20) : 0,
                  }}
                >
                  <ButtonLink
                    to={ctaLink}
                    styles={{
                      bg:
                        colorSet.bg === "darkPurple" ? "purple" : "darkPurple",

                      "&:hover": {
                        bg:
                          colorSet.bg === "purple" ||
                            colorSet.bg === "darkPurple"
                            ? "white"
                            : "purple",
                        color:
                          colorSet.bg === "purple" ||
                            colorSet.bg === "darkPurple"
                            ? "darkPurple"
                            : "white",
                      },
                    }}
                  >
                    {ctaLabel}
                  </ButtonLink>
                </div>
              )}
              {links ? (
                <LinksWrapper
                  sx={{
                    display: "flex",
                    flexDirection: ["column", "row"],
                  }}
                >
                  {links.map((item, i) => (
                    <div key={i}>
                      <IconLink {...item} type="arrowRight" />
                    </div>
                  ))}
                </LinksWrapper>
              ) : null}
            </div>

            {image && (
              <div
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: ["90%", null, "50%"],
                  mt: [rem(48), null, 0],
                }}
              >
                {image && (
                  <img
                    src={image}
                    alt=""
                    sx={{
                      width: "100%",
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </AccordionItemPanel>
      </AccordionItem>
    </PanelStyleWrapper>
  )
}
const Accordion = ({ items, heading, noPanelBg, charts, multiple = false }) => {
  if (heading) {
    return (
      <AccordionStylesWrapper>
        <div
          sx={{
            display: "flex",
            flexDirection: "row",
            width: "100%",
            maxWidth: ["100%", null, null, "66%"],
            px: [rem(48), null, null, 0],
            margin: "auto",
            flexWrap: "wrap",
          }}
        >
          <Themed.h2
            sx={{
              mb: rem(48),
              mt: 0,
              width: ["100%", null, null, `calc(40% - ${rem(50)})`],
              mr: rem(50),
            }}
          >
            {heading}
          </Themed.h2>
          <LibraryAccordion
            allowMultipleExpanded={multiple}
            allowZeroExpanded
            sx={{
              width: ["100%", null, null, "60%"],
            }}
          >
            {items.map((item, i) => (
              <Panel
                noMaxWidth
                key={item.heading}
                {...item}
                charts={charts}
                noBg={noPanelBg}
                body={formatMarkdownToHtml(item.body)}
                colorSet={colorSets[i % 4]}
              />
            ))}
          </LibraryAccordion>
        </div>
      </AccordionStylesWrapper>
    )
  }

  return (
    <AccordionStylesWrapper>
      <LibraryAccordion
        allowZeroExpanded
        allowMultipleExpanded={multiple}
      >
        {items.map((item, index) => (
          <Panel
            key={item.heading}
            {...item}
            charts={charts}
            noBg={noPanelBg}
            body={formatMarkdownToHtml(item.body)}
            colorSet={colorSets[index % 4]}
          />
        ))}
      </LibraryAccordion>
    </AccordionStylesWrapper>
  )
}

/**
 * @todo, button bg prop
 */
const colorSets = [
  {
    bg: "white",
    heading: "darkPurple",
    text: "darkPurple",
  },
  {
    bg: "white",
    heading: "darkPurple",
    text: "darkPurple",
  },
  {
    bg: "white",
    heading: "darkPurple",
    text: "darkPurple",
  },
  {
    bg: "white",
    heading: "darkPurple",
    text: "darkPurple",
  },
]

export default Accordion

Accordion.propTypes = {
  items: PropTypes.array,
}

const LinksWrapper = styled.div`
  div:not(:last-child) {
    margin-right: ${rem(47)};
  }
`
const AccordionStylesWrapper = styled.div`
  .accordion {
    border: none;
  }

  .accordion__button {
    position: relative;
    font-family: ${theme.fonts.heading};
    font-weight: 900;
    text-transform: uppercase;

    @media only screen and (max-width: ${theme.breakpoints[0]}) {
      font-size: ${rem(18)};
    }

    @media only screen and (min-width: ${theme
    .breakpoints[0]}) and (max-width: ${theme.breakpoints[1]}) {
      font-size: ${rem(32)};
    }

    @media only screen and (min-width: ${theme
    .breakpoints[1]}) and (max-width: ${theme.breakpoints[2]}) {
      font-size: ${rem(32)};
    }
    @media only screen and (min-width: ${theme.breakpoints[2]}) {
      font-size: ${rem(32)};
    }

    &:hover {
      background-color: transparent;
    }
  }
  .accordion__button:after {
    content: "+";
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);

    @media only screen and (min-width: ${theme.breakpoints[1]}) {
      right: 16px;
    }

    @media only screen and (min-width: ${theme.breakpoints[3]}) {
      right: 20px;
    }
  }

  .accordion__button[aria-expanded="true"]::after,
  .accordion__button[aria-selected="true"]::after {
    content: "−";
  }

  .accordion__button:before {
    display: none;
  }
`

const PanelStyleWrapper = styled.div`
  .accordion__button {
    color: ${p => `${theme.colors[p.colorSet.heading]}!important`};
    background: ${p => p.noBg ? 'transparent' : `${theme.colors[p.colorSet.bg]}!important`};
    /* text-transform: uppercase; */
    padding: 12px;
    padding-right: calc(12px * 2 + 16px);

    @media only screen and (min-width: ${theme.breakpoints[1]}) {
      padding: 16px;
      padding-right: calc(16px * 2 + 16px);
    }

    @media only screen and (min-width: ${theme.breakpoints[3]}) {
      padding: 20px;
      padding-right: calc(20px * 2 + 16px);
    }
  }

  .accordion__panel {
    padding: 0 12px 12px;

    @media only screen and (min-width: ${theme.breakpoints[1]}) {
      padding: 0 16px 16px;
    }

    @media only screen and (min-width: ${theme.breakpoints[3]}) {
      padding: 0 20px 20px;
    }

    p,
    li {
      a {
        color: ${p => `${theme.colors[p.colorSet.text]}`};

        &:hover {
          color: ${p => `${theme.colors.pink}`};
        }
      }
    }
  }
`
